// Design templates (consistency-pass spec §4): an index page over the
// workspace's templates, filtered in the browser (tens of rows), with a row
// menu per template.

import { Button, buttonVariants, DataTable, type DataTableColumn, SearchBar } from "@pej/kore/components";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Copy, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTemplates } from "@/api/templates";
import type { Template } from "@/api/types";
import { IndexPage } from "@/components/index-page/index-page";
import { TABLE_LABELS, tableError } from "@/components/page-states";
import { type RowActionDescriptor, RowActionsMenu } from "@/components/row-actions-menu";
import { filterRows } from "@/features/search/matches-search-rq";
import { DeleteTemplateDialog, deleteRefusal } from "@/features/templates/delete-template-dialog";
import { useDuplicateTemplate } from "@/features/templates/duplicate";
import { TEMPLATES_SEARCH_MANIFEST } from "@/features/templates/templates-filters";
import { countOf } from "@/lib/plural";

export const Route = createFileRoute("/w/$workspaceId/templates/")({
    component: TemplatesPage,
});

/** The Surveys list filtered to one template, its URL filter param. */
const usedBySearch = (templateId: string): Record<string, string> => ({ template: templateId });

function TemplatesPage() {
    const { workspaceId } = Route.useParams();
    const navigate = useNavigate();
    const templatesQuery = useTemplates(workspaceId);
    const duplicating = useDuplicateTemplate(workspaceId);
    const [search, setSearch] = useState("");
    const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);

    // Templates are tens of rows per workspace at most, so this list stays a
    // plain GET filtered in the browser — see
    // src/features/search/matches-search-rq.ts for why that's safe.
    const rows = filterRows(templatesQuery.data ?? [], { query: { value: search } }, (t) => [t.name]);

    // A link styled as a button (spec §2): cmd-click opens a tab. The header
    // and the empty state both offer it.
    const createButton = (
        <Link to="/w/$workspaceId/templates/new" params={{ workspaceId }} className={buttonVariants()}>
            <Plus aria-hidden="true" />
            Create template
        </Link>
    );

    const actions: RowActionDescriptor<Template>[] = [
        {
            id: "open",
            label: "Open",
            icon: Pencil,
            linkProps: (t) => ({
                to: "/w/$workspaceId/templates/$templateId",
                params: { workspaceId, templateId: t.id },
            }),
        },
        {
            id: "duplicate",
            label: "Duplicate",
            icon: Copy,
            disabledReason: () => (duplicating.isPending ? "Duplicating…" : undefined),
            onClick: (t) => void duplicating.duplicate(t),
        },
        {
            id: "delete",
            label: "Delete template",
            icon: Trash2,
            section: "danger",
            disabledReason: (t) => deleteRefusal(t.surveyCount),
            onClick: setDeleteTarget,
        },
    ];

    const columns: DataTableColumn<Template>[] = [
        {
            key: "name",
            header: "Name",
            cell: (template) => (
                <span className="flex items-center gap-2">
                    <span
                        aria-hidden="true"
                        className="inline-block size-4 shrink-0 rounded-sm border border-border"
                        style={{ backgroundColor: template.backgroundColor }}
                    />
                    <span className="font-medium">{template.name}</span>
                </span>
            ),
        },
        {
            key: "usedBy",
            header: "Used by",
            align: "right",
            cell: (template) =>
                template.surveyCount === 0 ? (
                    // A zero is muted text, not a link (nothing to open).
                    <span className="tabular-nums text-kore-text-muted">0</span>
                ) : (
                    // The surveys using it. The row itself opens the template
                    // on click and on Enter/Space: stopped here for both, so
                    // the link wins.
                    <Link
                        to="/w/$workspaceId/surveys"
                        params={{ workspaceId }}
                        search={usedBySearch(template.id)}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                        className="tabular-nums hover:underline"
                        aria-label={`Used by ${countOf(template.surveyCount, "survey")}`}
                    >
                        {template.surveyCount}
                    </Link>
                ),
        },
        {
            key: "actions",
            header: "",
            align: "right",
            cell: (template) => (
                <RowActionsMenu actions={actions} entity={template} label={`Actions for ${template.name}`} />
            ),
        },
    ];

    return (
        <IndexPage
            fill
            title="Templates"
            description="The shared look surveys reference — colors, logo, font, CSS."
            action={createButton}
        >
            {/* kore's own SearchBar, the same control the locations and screens
                pages render — one search box shape across the app, by being
                the same component rather than by matching its classes.
                `useKoreSearch` is deliberately skipped: that hook exists to
                back filters with the URL, and a query-only manifest has no
                filters to back, so a stubbed router adapter would be wiring
                that reads as load-bearing and is not. `filters={[]}` cannot
                change (no facet is declared, so no trigger can render), which
                is why `onFiltersChange` has nothing to do.

                SearchBar debounces `onQueryChange` at 200ms. For a
                browser-side filter over tens of rows that is a slight delay
                before the list narrows — the right trade for one consistent
                control, and not worth a bypass. */}
            <SearchBar
                manifest={TEMPLATES_SEARCH_MANIFEST}
                filters={[]}
                onFiltersChange={() => undefined}
                query={{ value: search, searchBy: null }}
                onQueryChange={(next) => setSearch(next.value)}
                // See the note on the other two lists: compact is the house
                // pair, and kore defaults to the roomier one.
                size="compact"
            />
            {/* No Card around it: kore's DataTable already renders a
                bordered, rounded surface, and wrapping it in one would stack
                two borders with a hairline above and below the rows. The
                table takes the remaining height and scrolls its own rows
                (`min-h-0 flex-1`; kore's DataTable root is flex-col so a
                height-constrained container pins its pager to the bottom). */}
            <DataTable
                className="min-h-0 flex-1"
                density="compact"
                caption="Templates"
                columns={columns}
                rows={rows}
                rowKey={(template) => template.id}
                // Error wins over loading: a retry leaves the previous failure
                // on screen while it flies.
                state={templatesQuery.isError ? "error" : templatesQuery.isLoading ? "loading" : undefined}
                error={tableError(templatesQuery.error, () => void templatesQuery.refetch())}
                labels={TABLE_LABELS}
                empty={
                    search.trim()
                        ? {
                              headline: "No templates match these filters.",
                              body: "Try a different search or clear the filters.",
                              action: (
                                  <Button variant="outline" size="sm" onClick={() => setSearch("")}>
                                      Clear filters
                                  </Button>
                              ),
                          }
                        : {
                              headline: "No templates yet.",
                              body: "Create a template to give surveys a shared look.",
                              action: createButton,
                          }
                }
                onRowClick={(template) =>
                    void navigate({
                        to: "/w/$workspaceId/templates/$templateId",
                        params: { workspaceId, templateId: template.id },
                    })
                }
            />
            {deleteTarget && (
                <DeleteTemplateDialog
                    key={deleteTarget.id}
                    template={deleteTarget}
                    open
                    onOpenChange={(open) => !open && setDeleteTarget(null)}
                    onDeleted={() => setDeleteTarget(null)}
                />
            )}
        </IndexPage>
    );
}
